import { useMemo, useState } from "react"
import { oneOfType, number, string, bool, shape, arrayOf, func } from "prop-types"
import AttendeeTypeRow from "./AttendeeTypeRow"
import { Button, StackView } from "@planningcenter/tapestry-react"
import { axiosRequest, api } from "utils/api"

const SignUpSelections = ({
  attendeeTypes: attendeeTypesFromProps = [],
  event: eventFromProps = {},
  simpleAttendees: simpleAttendeesFromProps = [],
  signUpPath,
  isEditing = true,
  handleEditClick,
  onSimpleAttendeeChange,
  registrationId,
  registrationIsCanceled = false,
  signupTimes = [],
  registerForSeparateTimesEnabled = false
}) => {
  const [event, setEvent] = useState(eventFromProps)

  const [attendeeTypes, setAttendeeTypes] = useState(attendeeTypesFromProps)

  const [simpleAttendees, setSimpleAttendees] = useState(simpleAttendeesFromProps)

  const setSimpleAttendeeAndTypes = (
    newAttendeeTypes,
    newSimpleAttendee,
    deletedSimpleAttendee = { id: 0 }
  ) => {
    const updatedSimpleAttendees = [
      ...simpleAttendees.filter(
        sa =>
          Number(sa.id) !== Number(newSimpleAttendee.id) &&
          Number(sa.id) !== Number(deletedSimpleAttendee.id)
      ),
      flattenAPIResponse(newSimpleAttendee)
    ]

    setSimpleAttendees(updatedSimpleAttendees)

    onSimpleAttendeeChange && onSimpleAttendeeChange(updatedSimpleAttendees)

    setAttendeeTypes(
      newAttendeeTypes.map(at =>
        flattenAPIResponse({
          ...at,
          activeAttendeesOnWaitlistCount: at.active_attendees_on_waitlist_count,
          anyActiveAttendeesOnWaitlist: at.any_active_attendees_on_waitlist
        })
      )
    )
  }

  const flattenAPIResponse = resp => {
    return {
      id: resp.id,
      ...resp.attributes,
      relationships: resp.relationships
    }
  }

  const updateSimpleAttendee = simpleAtt =>
    axiosRequest(
      simpleAtt.id ? "PATCH" : "POST",
      `events/${event.id}/${signUpPath}/simple_attendees/${simpleAtt.id || ""}`,
      simpleAtt
    ).then(res => res.data)

  const sendPromotionEmail = () =>
    api.post(`/registrations/${registrationId}/promote_simple_attendee_emails`)

  const fetchAttendeeTypes = () =>
    axiosRequest("get", `events/${event.id}/attendee_types?order=position`).then(res => res.data)

  const deleteSimpleAttendee = simpleAttendee =>
    axiosRequest(
      "DELETE",
      `events/${event.id}/${signUpPath}/simple_attendees/${simpleAttendee.id}`
    ).then(res => res.data)

  const patchAttendeeType = (attendeeTypeId, attributes) =>
    axiosRequest("PATCH", `events/${event.id}/attendee_types/${attendeeTypeId}`, {
      attributes
    }).then(res => res.data)

  const patchEvent = attributes =>
    axiosRequest("PATCH", `events/${event.id}`, { attributes }).then(res => res.data)

  const updateAttendeeTypeCapacity = (attendeeType, simpleAttendeeCount) => {
    const attendeeTypeCurrentMaxCapacity = attendeeType.maximumCapacity
      ? attendeeType.maximumCapacity
      : attendeeType.maximum_capacity
    const countToAdd =
      Number(attendeeTypeCurrentMaxCapacity) +
      (Number(simpleAttendeeCount) - Number(attendeeType.available_capacity))

    return patchAttendeeType(attendeeType.id, { maximum_capacity: countToAdd })
  }

  const updateEventCapacity = simpleAttendeeCount => {
    const countToAdd = Number(simpleAttendeeCount) + Number(event.currentActiveAttendees)

    return patchEvent({ maximum_capacity: countToAdd })
  }

  const updateAndDeleteSimpleAttendeeAPI = async (simpleAttendeeWaitlist, simpleAttendee) => {
    const simpleA = await updateSimpleAttendee({
      id: simpleAttendee.id,
      attributes: {
        count: Number(simpleAttendee.count) + Number(simpleAttendeeWaitlist.count)
      }
    }).then(res => {
      deleteSimpleAttendee(simpleAttendeeWaitlist)
      return res
    })

    const updatedAttendeeTypes = await fetchAttendeeTypes(simpleA.relationships.event.data.id)

    setSimpleAttendeeAndTypes(updatedAttendeeTypes, simpleA, simpleAttendeeWaitlist)
  }

  const deleteSimpleAttendeeAPI = async simpleAttendee => {
    const deletedSimpleSttendeeId = simpleAttendee.id
    const attendeeTypeId = simpleAttendee.relationships.attendee_type.data.id
    await deleteSimpleAttendee(simpleAttendee)

    const updatedAttendeeTypes = await fetchAttendeeTypes(attendeeTypeId)

    const updatedEvent = await axiosRequest("get", `events/${event.id}`).then(res => res.data)

    setEvent({
      id: updatedEvent.id,
      maximumCapacity: updatedEvent.attributes.maximum_capacity,
      currentActiveAttendees: updatedEvent.attributes.active_attendees_count,
      atMaximumCapacity: updatedEvent.attributes.at_maximum_capacity
    })

    setSimpleAttendeeAndTypes(updatedAttendeeTypes, { id: 0 }, { id: deletedSimpleSttendeeId })
  }

  const updateSimpleAttendeeAPI = async simpleAttendee => {
    const simpleA = await updateSimpleAttendee(simpleAttendee)

    const updatedAttendeeTypes = await fetchAttendeeTypes(simpleA.relationships.event.data.id)

    const updatedEvent = await axiosRequest("get", `events/${event.id}`).then(res => res.data)

    setEvent({
      id: updatedEvent.id,
      maximumCapacity: updatedEvent.attributes.maximum_capacity,
      currentActiveAttendees: updatedEvent.attributes.active_attendees_count,
      atMaximumCapacity: updatedEvent.attributes.at_maximum_capacity
    })

    setSimpleAttendeeAndTypes(updatedAttendeeTypes, simpleA)
  }

  const overCapacityWarningText = (
    isAttendeeTypeAtCapacity,
    isEventAtCapacity,
    attendeeType,
    simpleAttendeeCount
  ) => {
    const attendeeTypeCurrentMaxCapacity = attendeeType.maximumCapacity
      ? attendeeType.maximumCapacity
      : attendeeType.maximum_capacity
    if (isAttendeeTypeAtCapacity && !isEventAtCapacity) {
      const newMaxCapacity =
        Number(attendeeTypeCurrentMaxCapacity) +
        (Number(simpleAttendeeCount) - Number(attendeeType.available_capacity))
      return `This will cause ${attendeeType.name} to exceed its maximum capacity of ${attendeeTypeCurrentMaxCapacity}. When you make this promotion, the capacity will automatically increase to ${newMaxCapacity}.`
    } else if (!isAttendeeTypeAtCapacity && isEventAtCapacity) {
      const newMaxCapacity = Number(simpleAttendeeCount) + Number(event.currentActiveAttendees)
      return `This will cause your signup to exceed its maximum capacity of ${event.currentActiveAttendees}. When you make this promotion, the capacity will automatically increase to ${newMaxCapacity}.`
    } else if (isAttendeeTypeAtCapacity && isEventAtCapacity) {
      const attendeeTypeNewMaxCapacity =
        Number(attendeeTypeCurrentMaxCapacity) +
        (Number(simpleAttendeeCount) - Number(attendeeType.available_capacity))
      const eventNewMaxCapacity = Number(simpleAttendeeCount) + Number(event.currentActiveAttendees)
      return `This will cause your signup and ${attendeeType.name} to exceed their maximum capacities. When you make this promotion, your event capacity will automatically increase to ${eventNewMaxCapacity} and ${attendeeType.name} to ${attendeeTypeNewMaxCapacity}.`
    }
  }

  const handleWaitlistPromotion = simpleAttendee => {
    const {
      relationships: {
        attendee_type: {
          data: { id }
        }
      },
      count
    } = simpleAttendee

    const attendeeType = attendeeTypes.find(attType => Number(attType.id) === Number(id))

    const isAttendeeTypeAtCapacity =
      attendeeType.available_capacity != null && attendeeType.available_capacity < count
    const isEventAtCapacity =
      event.maximumCapacity != null &&
      event.maximumCapacity < Number(count) + Number(event.currentActiveAttendees)

    if (isAttendeeTypeAtCapacity || isEventAtCapacity) {
      return alertModal(
        {
          title: "Promote and exceed capacity?",
          type: "warning",
          text: overCapacityWarningText(
            isAttendeeTypeAtCapacity,
            isEventAtCapacity,
            attendeeType,
            simpleAttendee.count
          ),
          closeOnConfirm: true,
          buttons: {
            cancel: {
              text: "Nevermind",
              value: null,
              visible: true,
              className: "",
              closeModal: true
            },
            promoteAll: {
              text: "Promote all attendees",
              value: "promoteAll",
              visible: true,
              className: "swal-button--confirm",
              closeModal: true
            }
          }
        },
        buttonValue => {
          if (buttonValue === "promoteAll") {
            Promise.all([
              isEventAtCapacity ? updateEventCapacity(simpleAttendee.count) : Promise.resolve(),
              isAttendeeTypeAtCapacity
                ? updateAttendeeTypeCapacity(attendeeType, simpleAttendee.count)
                : Promise.resolve()
            ]).then(_ => processSimpleAttendeeChange(simpleAttendee))
          }
        }
      )
    } else {
      return processSimpleAttendeeChange(simpleAttendee)
    }
  }

  const processSimpleAttendeeChange = simpleAttendee => {
    const activeAttendee = simpleAttendeeByAttendeeType(
      simpleAttendee.relationships.attendee_type.data.id
    )

    if (!activeAttendee) {
      return updateSimpleAttendeeAPI({
        id: simpleAttendee.id,
        attributes: { count: simpleAttendee.count, waitlist: false }
      }).then(_ => sendPromotionEmail())
    } else {
      return updateAndDeleteSimpleAttendeeAPI(simpleAttendee, activeAttendee).then(_ =>
        sendPromotionEmail()
      )
    }
  }

  const handleSimpleAttendeeChange = (attendeeType, count) => {
    const simpleAttendee = simpleAttendees.find(
      sa =>
        sa.relationships &&
        Number(sa.relationships.attendee_type.data.id) === Number(attendeeType.id) &&
        sa &&
        sa.waitlist === attendeeType.isWaitlist
    ) || { type: "SimpleAttendee", count, attributes: {} }

    simpleAttendee.attributes = {
      count: Number(count),
      waitlist: attendeeType.isWaitlist
    }
    simpleAttendee.relationships = {
      attendee_type: { data: { type: "AttendeeType", id: attendeeType.id } }
    }

    setSimpleAttendees([
      ...simpleAttendees.filter(sa => Number(sa.id) !== Number(simpleAttendee.id)),
      flattenAPIResponse(simpleAttendee)
    ])

    const { type, id, attributes, relationships } = simpleAttendee
    if (count == 0) {
      deleteSimpleAttendeeAPI(simpleAttendee)
    } else {
      updateSimpleAttendeeAPI({ type, id, attributes, relationships })
    }
  }

  const simpleAttendeeByAttendeeType = (attendeeTypeId, isWaitlist = false) =>
    simpleAttendees.find(sa => {
      const {
        waitlist = false,
        relationships: { attendee_type: { data: { id } = {} } = {} } = {}
      } = sa

      return Number(id) === Number(attendeeTypeId) && waitlist === isWaitlist
    })

  const valueForSelects = useMemo(() => {
    let values = {}

    attendeeTypes.map(attendeeType => {
      const sa = simpleAttendeeByAttendeeType(attendeeType.id)
      values[attendeeType.id] = sa ? Number(sa.count) : 0
    })

    return values
  }, [attendeeTypes])

  const maxSelects = useMemo(() => {
    let types = {}

    attendeeTypes.map(attendeeType => {
      types[attendeeType.id] =
        attendeeType.constrained_capacity ||
        attendeeType.at_maximum_capacity ||
        event.atMaximumCapacity
          ? Math.min(21, valueForSelects[attendeeType.id] + attendeeType.constrained_capacity + 1)
          : 21
    })
    return types
  }, [valueForSelects])

  const groupAttendeeTypes = () => {
    return attendeeTypes.reduce(
      (obj, at) => {
        const saStandard = simpleAttendeeByAttendeeType(at.id)
        const saWaitlist = simpleAttendeeByAttendeeType(at.id, true)
        const standard = [
          ...(obj["standard"] || []),
          {
            ...at,
            isWaitlist: false,
            simpleAttendee: saStandard || null,
            selectedCount: saStandard ? Number(saStandard.count) : 0
          }
        ]
        const waitlist = [
          ...(obj["waitlist"] || []),
          {
            ...at,
            isWaitlist: true,
            simpleAttendee: saWaitlist || null,
            selectedCount: saWaitlist ? Number(saWaitlist.count) : 0
          }
        ]
        return {
          ...obj,
          standard,
          ...(at.waitlist && { waitlist })
        }
      },
      { standard: [], waitlist: [] }
    )
  }

  const groupedAttendeeTypes = groupAttendeeTypes()

  let groupedSignupTimes = []

  const signupTimeAttendeeTypes = signupTime => attendeeType =>
    attendeeType.relationships.signup_time &&
    attendeeType.relationships.signup_time.data &&
    attendeeType.relationships.signup_time.data.id == signupTime.id

  if (registerForSeparateTimesEnabled) {
    groupedSignupTimes = signupTimes.map(signupTime => ({
      signupTime,
      attendeeTypes: {
        standard: groupedAttendeeTypes.standard.filter(signupTimeAttendeeTypes(signupTime)),
        waitlist: groupedAttendeeTypes.waitlist.filter(signupTimeAttendeeTypes(signupTime))
      }
    }))
  } else {
    groupedSignupTimes = [
      {
        signupTime: { id: -1 },
        attendeeTypes: {
          standard: groupedAttendeeTypes.standard,
          waitlist: groupedAttendeeTypes.waitlist
        }
      }
    ]
  }

  return (
    <section className="pr p-2" style={{ zIndex: 2 }}>
      <StackView
        axis="horizontal"
        alignmewnt="center"
        distribution="space-between"
        marginBottom={2}
      >
        <h1 className="h4 fw-600 mb-0">Selections</h1>
        {handleEditClick && !registrationIsCanceled && (
          <Button
            title={isEditing ? "Done" : "Edit"}
            size="xs"
            variant={isEditing ? null : "outline"}
            theme={isEditing ? "create" : "default"}
            onClick={handleEditClick}
          />
        )}
      </StackView>
      {groupedSignupTimes.map((group, index) => {
        const { signupTime, attendeeTypes } = group

        const totalWaitlisted = attendeeTypes.waitlist.reduce(
          (acc, item) => acc + item.selectedCount,
          0
        )

        const signUpCount = Object.values(attendeeTypes)
          .flat(1)
          .reduce((accum, item) => accum + item.selectedCount, 0)

        const showDateTime = isEditing || (!isEditing && signUpCount > 0)

        return (
          <div
            key={signupTime.id}
            className={`${showDateTime ? "mt-2" : ""} ${
              showDateTime && index > 0 ? "pt-2 line" : ""
            }`}
          >
            {showDateTime && (
              <h2 className="h4 fw-600 c-neutral-700 mb-1">{signupTime.dateTimeToString}</h2>
            )}
            <div>
              {attendeeTypes.standard.map(attendeeType => (
                <AttendeeTypeRow
                  key={`${signupTime.id}-${attendeeType.id}`}
                  attendeeType={attendeeType}
                  maxSelectValue={maxSelects[attendeeType.id]}
                  eventAtMaximumCapacity={event.atMaximumCapacity}
                  onSimpleAttendeeChange={handleSimpleAttendeeChange}
                  isEditing={isEditing}
                />
              ))}
            </div>
            {attendeeTypes.waitlist.length > 0 &&
              (isEditing || (!isEditing && totalWaitlisted > 0)) && (
                <div className="mt-2 action-drawer">
                  <div>
                    <h2 className="h5 tt-u">Waitlist</h2>
                  </div>
                  <div>
                    {attendeeTypes.waitlist.map(attendeeType => (
                      <AttendeeTypeRow
                        key={`${signupTime.id}-${attendeeType.id}`}
                        attendeeType={attendeeType}
                        maxSelectValue={20}
                        eventAtMaximumCapacity={event.atMaximumCapacity}
                        onSimpleAttendeeChange={handleSimpleAttendeeChange}
                        isEditing={isEditing}
                        onWaitlistPromotion={handleWaitlistPromotion}
                      />
                    ))}
                  </div>
                </div>
              )}
          </div>
        )
      })}
    </section>
  )
}

SignUpSelections.propTypes = {
  event: shape({}),
  attendeeTypes: arrayOf(
    shape({
      id: oneOfType([number, string]),
      name: string,
      price_string: string,
      at_maximum_capacity: bool,
      available_capacity: number
    })
  ),
  simpleAttendees: arrayOf(
    shape({
      id: oneOfType([number, string]),
      count: number
    })
  ),
  onSimpleAttendeeChange: func,
  signUpPath: string,
  isEditing: bool,
  handleEditClick: func,
  registrationIsCanceled: bool,
  registrationId: number,
  signupTimes: arrayOf(
    shape({
      id: number,
      dateTimeToString: string
    })
  ),
  registerForSeparateTimesEnabled: bool
}

export default SignUpSelections
